import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import { IPagedResult } from '../../shared/models/paged-result';
import { IZone, IZoneWithUbigeoCodes } from '../../shared/models/core/zone';
import { Ubigeo } from '../../shared/models/catalog/ubigeo';
import { IZoneFilterSet } from '../../pages/management/zones/hooks/useZoneList';
import { serialize } from '../../utility/serialize';

type CreateZonePayload = Omit<IZoneWithUbigeoCodes, 'id'>;

export const zoneApi = createApi({
  reducerPath: 'zoneApi',
  baseQuery,
  tagTypes: ['Zones', 'Ubigeos'],
  endpoints: (builder) => ({
    getZones: builder.query<IPagedResult<IZone>, IZoneFilterSet>({
      query: (filterSet) => ({
        url: `/api/zones?${serialize(filterSet)}`,
        method: 'GET',
      }),
    }),
    getZone: builder.query<IZoneWithUbigeoCodes, string>({
      query: (id) => ({
        url: `/api/zones/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'Zones', id }],
    }),
    addZone: builder.mutation<IZone, CreateZonePayload>({
      query: (values) => ({
        url: `/api/zones`,
        method: 'POST',
        body: values,
      }),
      invalidatesTags: ['Zones'],
    }),
    updateZone: builder.mutation<IZone, Partial<IZoneWithUbigeoCodes>>({
      query: (values) => ({
        url: `/api/zones/${values.id}`,
        method: 'PUT',
        body: values,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Zones', id }],
    }),
    deleteZone: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/zones/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Zones', 'Ubigeos'],
    }),
    // New endpoint to get all ubigeos, potentially with a filter for unassigned ones
    getUnassignedUbigeos: builder.query<Ubigeo[], void>({
      query: () => ({
        url: `/api/ubigeos?hasZoneId=false`,
        method: 'GET',
      }),
      providesTags: ['Ubigeos'],
    }),
  }),
});

export const {
  useGetZonesQuery,
  useLazyGetZonesQuery,
  useAddZoneMutation,
  useGetZoneQuery,
  useLazyGetZoneQuery,
  useUpdateZoneMutation,
  useDeleteZoneMutation,
  useGetUnassignedUbigeosQuery,
} = zoneApi;
